import { bufferToHexString } from '@iotize/common/byte-converter';
import {
  BodyDecoder,
  ResultCode,
  TapRequestFrame,
  TapResponseFrame,
} from '@iotize/tap/client/api';
import { TapResponseFrameBuilder } from '@iotize/tap/client/impl';

import { ResponseInterface } from './response-interface';
import { TapError } from './tap-errors';

export class TapResponse<DataType> implements ResponseInterface<DataType> {
  /**
   *
   */
  private _bodyCache?: DataType;

  private error?: Error;

  static SUCCESS(data?: Uint8Array): TapResponse<any> {
    return TapResponse.create(ResultCode.OK, data);
  }

  static ERROR(errorCode?: number): TapResponse<any> {
    errorCode = errorCode || ResultCode.BAD_REQUEST;
    return TapResponse.create(errorCode);
  }

  public static create(codeRet: number, data?: Uint8Array): TapResponse<any> {
    return new TapResponse<any>(TapResponseFrameBuilder.create(codeRet, data));
  }

  private get data() {
    return this._tapResponse.data;
  }

  constructor(
    private _tapResponse: TapResponseFrame,
    public request?: TapRequestFrame,
    private bodyDecoder?: BodyDecoder<DataType>,
  ) {}

  public body<T = DataType>(decoder?: BodyDecoder<T>): T {
    this.successful();
    if (decoder) {
      return decoder.decode(this.rawBody());
    } else if (this._bodyCache === undefined) {
      if (this.bodyDecoder) {
        this._bodyCache = this.bodyDecoder.decode(this.rawBody());
      } else {
        this._bodyCache = this.rawBody() as any;
      }
    }
    // In this case we now that T = DataType
    return this._bodyCache as unknown as T;
  }

  public rawBody(): Uint8Array {
    if (this.data) {
      return this.data;
    } else {
      return new Uint8Array(0);
    }
  }

  public setBodyDecoder(decoder: BodyDecoder<DataType> | undefined) {
    this.bodyDecoder = decoder;
    return this;
  }

  public hasBody(): boolean {
    return this.rawBody() != null;
  }

  public codeRet(): number {
    return this.status;
  }

  get status() {
    return this._tapResponse.status;
  }

  public toString(): string {
    return `ResponseMessage[codeRet="${this.status}";data="${
      this.hasBody() ? bufferToHexString(this.rawBody()) : 'NO DATA'
    }";decoder=${
      this.bodyDecoder ? this.bodyDecoder.constructor.name : 'NONE'
    }]`;
  }

  public toBytes(): Uint8Array {
    return this.data;
  }

  public setBody(body: DataType | undefined) {
    this._bodyCache = body;
  }

  public isSuccessful(): boolean {
    return (this.status & 0x80) === 0;
  }

  public setError(err: Error) {
    return (this.error = err);
  }

  public successful() {
    if (!this.isSuccessful()) {
      if (this.error) {
        throw this.error;
      } else {
        throw TapError.reponseStatusError(this);
      }
    }
  }

  public setRequest(request: TapRequestFrame) {
    this.request = request;
  }
}

export { TapResponse as ServiceResponse };
